import * as React from "react";
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {
  addExistSubject,
  deleteParticipant, getParticipantsWithRole,
  getRepresentativeList,
  getSubjectListDocuments,
  saveParticipantFormValue
} from "../participant";
import {useNotify} from "../../../domains/common/components/AlertNotify";
import {
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  IndentSpace,
  PlainText,
  Title4,
  Title5,
} from "../../../domains/common/components/Typography";
import {
  errorsDescription,
  exceptionsTitle,
  infoTooltip,
  rightKindSelect,
  roleInfoMapping,
  RolesTitle,
} from "../../../dictionaries";
import {
  ErrorTooltip,
  InfoTooltip,
} from "../../../domains/common/components/Tooltip";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import {
  RegistrationTypes,
  RolesEnums,
  SubjectType,
  SubjectTypeTitle,
} from "../../../enums";
import {
  hasParticipantsError,
  optionalParticipantErrors,
  participantsErrors,
  validateOptionalParticipantElements,
  validatePersonData,
} from "../../../validation";
import { waitInputComplete } from "../../../fn";
import { hasStatus } from "../status";
import { DealActionsEnums, handleDealAction } from "../../../actions";
import {
  ButtonBase,
  ButtonGroupCustom,
} from "../../../domains/common/components/Button";
import { dealData } from "../deal";
import { CrossIcon } from "../../../domains/common/components/SvgIcons";
import { OrganizationForm } from "./OrganizationForm";
import DeleteIcon from "@mui/icons-material/Delete";
import { PersonForm } from "./PersonForm";
import { getDocumentsList } from "../documents";
import FormSelect, {
  FormSelectCtrl,
} from "../../../domains/common/components/FormSelect";
import { FormInputCtrl } from "../../../domains/common/components/FormInput";
import { Search } from "@mui/icons-material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useParticipants } from "../useParticipants";
import SearchSubject from "./SearchSubject";
import Tab from "@mui/material/Tab";
import { DealPageContext } from "../index";
import { SubjectSignmeSignature } from "../../../domains/sign/components/SignmeComponents";
import { SubjectMydssSignatureComponent } from "../../../domains/sign/components/MydssComponents";
import { CustomTabPanel } from '../../../domains/common/components/Tabs';
import { Link } from '@mui/material';
import { userOptions } from '../../../user';
import { OpenInNew  } from '@mui/icons-material';


export const ParticipantsContext = createContext();

const ParticipantsProvider = ({role, children}) => {
  const {participants, handleAddExistSubject, addNewParticipant, addGuarantor, handleAddRep, onDelete, onChange} = useParticipants({role});

  const [participantSearchContainer, toggleDrawerSearchContainer] = ParticipantSearchDrawer({onSelectSubject: handleAddExistSubject})

  const participantsValues = useMemo(() => ({
    participants, handleAddExistSubject, addNewParticipant, addGuarantor, role, onDelete, toggleDrawerSearchContainer, handleAddRep, onChange
  }), [participants]);

  return (
    <ParticipantsContext.Provider value={participantsValues}>
      {children}
      {participantSearchContainer}
    </ParticipantsContext.Provider>
  );
};

/**
 * Список участников сделки в зависимсоти от роли
 * @param role
 * @returns {Element}
 * @constructor
 */
export function ParticipantsComponent({role}) {
  return (
    <ParticipantsProvider role={role}>
      <Grid container spacing={2}>
        <Grid item xs={8} sx={{display: "flex"}}>
          <Title5 title={RolesTitle[role]}/>
          {
            roleInfoMapping[role] ?
              <>
                <IndentSpace size={5}/>
                <InfoTooltip placement="bottom" title={infoTooltip.EXT_SIGNER} sx={{pt: '4px'}}/>
              </> : void 0
          }
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end">
            <AddSubjectButton />
          </Box>
        </Grid>
        <Grid item container xs={12} spacing={1}>
          <ParticipantsList/>
        </Grid>
      </Grid>
    </ParticipantsProvider>
  )
}

export const ParticipantsList = () => {
  const {participants} = useContext(ParticipantsContext);

  return (<>
    {participants.map((value) => {
      return <Grid item xs={12} sx={{display: 'flex'}}>
        {
          hasParticipantsError(value.participantId) ?
            <>
              <ErrorTooltip placement="bottom" title={errorsDescription.PARTICIPANT_COMMON} sx={{pt: '2px'}}/>
            </> : <IndentSpace size="6px"/>
        }
        <ParticipantItem values={value} />
      </Grid>
    })}
  </>)
}

/**
 * Участник сделки в списке участников
 * @param {Participant} values
 * @param onDelete
 * @returns {Element}
 * @constructor
 */
export function ParticipantItem({values}) {
  const {onDelete, onChange} = useContext(ParticipantsContext);
  const {toggleDrawer} = useContext(DealPageContext);
  const [drawerState, setDrawerState] = useState(false);
  const {openNotify} = useNotify();

  const theme = useTheme();

  let hasChange = false
  const subjectId = values.subjectId;
  const participantId = values.participantId;
  const personContainerTitleId = `personContainerTitleId${subjectId}`;

  const onDeleteParticipant = () => {
    onDelete(participantId);
  }

  const savePersonForm = async () => {
    if (!hasChange) {
      setDrawerState(false);
      return;
    }

    if (hasStatus('dealSentToRR')) {
      openNotify({msg: exceptionsTitle.DEAL_ALREADY_SENTTO_RR, duration: 3000, type: 'error'})
      setDrawerState(false);
      return;
    }

    const result = await saveParticipantFormValue(participantId, subjectId);

    if (result.ok) {
      openNotify({msg: "Данные успешно сохранены", duration: 1500, type: 'success'})
      handleDealAction(DealActionsEnums.CHANGE_PARTICIPANT);
      setDrawerState(false);
      hasChange = false;
    } else {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "При сохранении возникла ошибка",
        duration: 10000,
        errors: result.errors,
        type: 'error',
        positionH: 'right',
      })
    }
  }

  const validateParticipantsErrors = () => {
    const errors = validatePersonData(values)
    const errorsOpt = validateOptionalParticipantElements(values)

    participantsErrors.set(participantId, {...errors, ...errorsOpt});
  }

  const onChangePersonForm = () => { hasChange = true; }

  const waitChangeForm = waitInputComplete(onChangePersonForm, 300);

  const openDrawer = () => {
    toggleDrawer(true, (
      <ParticipantFormComponent values={values} onDelete={onDelete} onChange={waitChangeForm} savePersonForm={savePersonForm}/>
    ), () => {
      validateParticipantsErrors();
      savePersonForm();
      onChange ? onChange() : void 0;
    })();
  }


  const getShare = () => {
    if(values.rightKind !== 'ownership'){
      return values?.share
    }
  }

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={10} sx={{height: 'auto', minHeight: '30px', pl: '10px', pt: '2px'}}>
          <div id={personContainerTitleId}>
            <Box display="inline" style={{cursor: 'pointer'}} onClick={openDrawer}>
              <PlainText>{getFIO(values)}</PlainText>
            </Box>
          </div>
        </Grid>
        <Grid item xs={1} sx={{verticalAlign: 'top'}}><PlainText>{getShare()}</PlainText></Grid>
        <Grid item xs={1} sx={{verticalAlign: 'top'}}>
          <IconButton size="small" sx={{padding: 0, color: theme.palette.icon.default}}
                      onClick={async () => {
                        const result = await deleteParticipant(participantId);
                        if (result.ok) {
                          handleDealAction(DealActionsEnums.DELETE_PARTICIPANT)
                          onDeleteParticipant ? onDeleteParticipant() : void 0;
                        }
                      }}>
            <DeleteIcon fontSize="inherit"/>
          </IconButton>
        </Grid>
      </Grid>
    </>
  )
}

export const ParticipantFormComponent = ({values, onChange, onDelete, savePersonForm}) => {
  const [activeTab, setActiveTab] = useState(0);
  const subjectId = values.subjectId;
  const participantId = values.participantId;
  const personContainerTitleId = `personContainerTitleId${subjectId}`;
  const {openNotify} = useNotify();
  const {closeDealDrawer} = useContext(DealPageContext);

  const handleTabChange = (event, newValue) => {
    if(event.target.id !== 'simple-tab-0')
      savePersonForm();

    setActiveTab(newValue);
  };

  const closeWOSave = () => {
    openNotify({msg: "Данные не были сохранены", duration: 2000, type: 'warning'})
    closeDealDrawer();
  }

  const onDeleteParticipant = () => {
    onDelete(participantId);
    closeDealDrawer();
  }

  const tabProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const optionElements = <ParticipantOptionalElements role={values.role} values={values} onChange={onChange}/>

  return (
    <Grid container spacing={0} alignItems="center" justifyContent="center">
      <Grid item xs={11} display="inline" sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{fontWeight: 300, marginBottom: 0}} variant="h6" gutterBottom display="inline">
          {getFIO(values)}
        </Typography>
        <Link
          href={`/subject?id=${values.subjectId}`}
          target={userOptions?.openLinkCurrentTab ? "" : "_blank"}
          rel="noopener noreferrer"
          sx={{
            ml: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontWeight: 500,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          }}
        >
          <OpenInNew fontSize="small" sx={{ verticalAlign: 'middle' }} />
        </Link>
      </Grid>
      <Grid item xs={1} display="inline">
        <ButtonBase onClick={closeWOSave} variant="text">
          <CrossIcon size={20}/>
        </ButtonBase>
      </Grid>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="basic tabs example">
            <Tab label="Анкета" {...tabProps(0)} />
            {values.type === SubjectType.PERSON ? <Tab label="Выпустить SignMe" {...tabProps(1)} disabled={dealData.configs.isSignMeDisabled} /> : null}
            {values.type === SubjectType.PERSON ? <Tab label="Выпустить MyDSS" {...tabProps(2)} disabled={dealData.configs.isMyDSSDisabled || values.type === SubjectType.ORGANIZATION}/> : null}
          </Tabs>
        </Box>
        <Box style={{height: 20}}></Box>
        <CustomTabPanel value={activeTab} index={0}>
            {
              values.type === SubjectType.PERSON ?
                <PersonForm personId={subjectId} values={values} optionElements={optionElements}
                            onChange={onChange} onDelete={onDeleteParticipant}/> : void 0
            }
            {
              values.type === SubjectType.ORGANIZATION ?
                <OrganizationForm values={values} personId={values.subjectId}
                                  onChange={onChange} optionElements={optionElements}
                                  onDelete={onDeleteParticipant}></OrganizationForm> : void 0
            }
        </CustomTabPanel>
        <CustomTabPanel value={activeTab} index={1}>
          <SubjectSignmeSignature subjectId={values.subjectId} />
        </CustomTabPanel>
        <CustomTabPanel value={activeTab} index={2}>
          <SubjectMydssSignatureComponent subjectId={values.subjectId} />
        </CustomTabPanel>
      </Box>
    </Grid>
  )
}

function ParticipantOptionalElements({role, values, onChange}) {
  const [error, setError] = useState();
  const [participantIdx, setParticipantIdx] = useState(null);
  const [optionalValues, setOptionalValues] = useState(values);

  useEffect(() => {
    const participantIdx = dealData.participants.findIndex((item) => item.participantId === values.participantId)
    setParticipantIdx(participantIdx);

    const participant = dealData.participants[participantIdx]

    validateOptionalParticipantElements(participant)

    setError(optionalParticipantErrors)
  }, []);

  const optionalElements = []

  const handleChange = (e) => {
    onChange ? onChange() : void 0;

    const name = e.target.name;
    const value = e.target.value;

    const participant = dealData.participants[participantIdx];
    participant[name] = value;

    setOptionalValues({...participant});

    validateOptionalParticipantElements(participant)
    setError(optionalParticipantErrors)
  };

  if ([
    RolesEnums.SELLER, RolesEnums.BUYER,
    RolesEnums.DECLARANT, RolesEnums.DEVELOPER, RolesEnums.SHAREHOLDER,
    RolesEnums.PLEDGER, RolesEnums.PLEDGEE, RolesEnums.LANDLORD, RolesEnums.RENTER,
    RolesEnums.OLD_OWNER, RolesEnums.NEW_OWNER
  ].includes(role)) {
    optionalElements.push(
      <>
        <Grid item xs={12} container spacing={0} sx={{mt: '5px'}}>
          <RightKindContainer values={optionalValues} onChange={handleChange} error={error}/>
        </Grid>
        <Grid item xs={12} container spacing={0} sx={{mt: '5px'}}>
          <RepresentativeRefContainer values={optionalValues} onChange={handleChange} error={error}/>
        </Grid>
        <Grid item xs={12} container spacing={0} sx={{mt: '5px'}}>
          <SubjectDocumentsRef values={optionalValues} participantId={optionalValues.participantId} error={error}/>
        </Grid>
      </>
    )
  }

  return optionalElements;
}

function getFIO(values) {
  if (values.type === SubjectType.PERSON) {
    const lastName = values?.lastName || '';
    const firstName = values?.firstName || '';
    const middleName = values?.middleName || '';

    if (!lastName && !firstName && !middleName)
      return SubjectTypeTitle.PERSON;

    return `${lastName} ${firstName} ${middleName}`;
  } else if (values.type === SubjectType.ORGANIZATION) {
    if (values.name)
      return values.name;

    return SubjectTypeTitle.ORGANIZATION;
  }
}

export const RightKindContainer = ({values, onChange, error}) => {
  return (
    <Grid item xs={12} container spacing={0}>
      <Grid item xs={12}><Title4 title="Право собственности"/></Grid>
      <Grid item xs={6}><FormSelectCtrl data={rightKindSelect} label="Вид права" minWidth="400"
                                        onChange={onChange}
                                        value={values?.rightKind || 'ownership'}
                                        name="rightKind" emptyOpt={true}/></Grid>
      <Grid item xs={6}>
        {
          values?.rightKind === 'ownership' ?
            '' :
            <FormInputCtrl label="Доля в праве" name="share" value={values?.share} onChange={onChange}
                           error={error?.share || error?.sharedOwnership}/>
        }
        {
          error?.share ?
            <>
              <IndentSpace size={5}/>
              <ErrorTooltip placement="bottom" title={errorsDescription.PARTICIPANT_SHARE_INCORRECT}/>
            </> : void 0
        }
        {
          error?.sharedOwnership ?
            <>
              <IndentSpace size={5}/>
              <ErrorTooltip placement="bottom" title={errorsDescription.PARTICIPANT_SHAREDOWNERSHIP_INCORRECT}/>
            </> : void 0
        }
      </Grid>
    </Grid>
  )
}

/**
 * Блок выбора представителя для участника сделки
 * @param values
 * @param onChange
 * @param error
 * @param onChange
 * @param error
 * @returns {Element}
 * @constructor
 */
export function RepresentativeRefContainer({values, onChange, error}) {
  let [docs] = useState(getDocumentsList());
  let [representative, setRepresentative] = useState(getRepresentativeList());

  return (
    <Grid item xs={12} container spacing={0}>
      <Grid item xs={12}><Title4 title="Представитель"/></Grid>
      <Grid item xs={6}>
        <FormSelect data={representative}
                    label="Выберите представителя"
                    minWidth="400"
                    value={values?.representativeId}
                    disabled={representative?.length ? false : true}
                    onChange={onChange}
                    name="representativeId"
        />
      </Grid>
      <Grid item xs={6}>
        <FormSelectCtrl data={docs}
                        label="Документ подтверждающий полномочия"
                        minWidth="400"
                        value={values?.representativeDocumentId}
                        keyToInt={true}
                        onChange={onChange}
                        disabled={representative?.length ? false : true}
                        name="representativeDocumentId"
                        error={error?.represDocumentAttachToParticipant || error?.represDocSetToParticipant}
        />
        {
          error?.represDocumentAttachToParticipant ?
            <>
              <IndentSpace size={5}/>
              <ErrorTooltip placement="bottom" title={errorsDescription.PARTICIPANT_REPRES_DOC_NOT_ATTACHED}/>
            </> : void 0
        }
        {
          error?.represDocSetToParticipant ?
            <>
              <IndentSpace size={5}/>
              <ErrorTooltip placement="bottom" title={errorsDescription.PARTICIPANT_REPRES_DOC_NOT_SET}/>
            </> : void 0
        }
      </Grid>
    </Grid>
  )
}

export function SubjectDocumentsRef({participantId}) {
  let [docList, setDocList] = useState(getSubjectListDocuments({participantId}));

  return (
    <Grid item xs={10} container spacing={0}>
      <Grid item xs={12}><Title4 title="Документы участника"/></Grid>
      <Grid item xs={12}>
        <List>
          {
            docList.map((document) => {
                return <ListItem>
                  <ListItemAvatar><InsertDriveFileOutlinedIcon></InsertDriveFileOutlinedIcon></ListItemAvatar>
                  <ListItemText primary={document.fileName} secondary={document.fileDesc} sx={{fontSize: 6}}
                                primaryTypographyProps={{
                                  fontSize: 13,
                                }}
                                secondaryTypographyProps={{
                                  fontSize: 11,
                                }}
                  />

                </ListItem>
              }
            )
          }
        </List>
      </Grid>
    </Grid>
  )
}

export function AddSubjectButton() {
  const {addNewParticipant, addGuarantor, role, toggleDrawerSearchContainer} = useContext(ParticipantsContext);
  const excludeLegal = [RolesEnums.REPRESENTATIVE, RolesEnums.EXT_SIGNER].includes(role);

  let options;

  if (excludeLegal) {
    options =
      [
        {key: 'person', value: 'Физическое лицо'},
        {
          key: 'organization', value: (<><Search/> Поиск клиента</>),
          onClick: () => toggleDrawerSearchContainer(true)
        }
      ]
  } else {
    options = [
      {key: 'person', value: 'Физическое лицо'},
      {key: 'organization', value: 'Юридическое лицо'},
      {
        key: 'organization', value: (<><Search/> Поиск клиента</>),
        onClick: () => toggleDrawerSearchContainer(true)
      },
    ]
  }

  try {
    // Гарант сделки, перечень в таблице conf
    // только для Дополнительного заявления
    if (
      dealData.type === RegistrationTypes.ADDITIONAL_STATEMENT &&
      dealData.configs?.guarantors.length &&
      !excludeLegal
    ) {
      dealData.configs?.guarantors.forEach((v) => {
        options.push({
          ...v,
          onClick: () => {
            addGuarantor(v.key)
          }
        });
      })
    }
  } catch (e) {
  }

  const handleClick = () => {
    addNewParticipant('person')
  };

  const handleMenuItemClick = (type, index) => {
    addNewParticipant(type)
  };

  return (
    <ButtonGroupCustom onButtonClick={handleClick} onMenuClick={handleMenuItemClick} options={options}/>
  );
}

/**
 * Дровер с поиском клиента
 * @param onSelectSubject
 * @return {(React.JSX.Element|drawerToggle)[]}
 * @constructor
 */
export function ParticipantSearchDrawer({onSelectSubject}) {
  const [drawerState, setDrawerState] = useState(false);

  const drawerToggle = (state) => {
    setDrawerState(state)
  }

  const closeWOSave = () => setDrawerState(false);

  const onClickToSubject = (subject) => {
    onSelectSubject ? onSelectSubject(subject) : void 0;

    closeWOSave();
  }

  return [<Drawer open={drawerState} anchor='right'>
    <Container sx={{width: 600, padding: 1}}>
      <Grid container spacing={0} alignItems="center" justifyContent="left">
        <Grid item xs={11} display="inline"></Grid>
        <Grid item xs={1} display="inline"><ButtonBase onClick={closeWOSave}><CrossIcon size={20}/></ButtonBase></Grid>
        <Grid item xs={12} sx={{width: 500}}><SearchSubject onSelect={onClickToSubject}/></Grid>
      </Grid>
    </Container>
  </Drawer>, drawerToggle]
}

